import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'

import * as api from '../api/snapshot.api.js'
import { ApiError } from '../api/client.js'
import { useAuth } from '../auth/AuthProvider.tsx'

function pesan(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Tidak dapat menghubungi server'
  const detail = error.details?.map((d) => `${d.field}: ${d.message}`).join('; ')
  return detail !== undefined && detail !== '' ? detail : error.message
}

const STATE_CLASS: Record<api.SnapshotState, string> = {
  DISUSUN: 'badge-draf',
  TERSEGEL: 'badge-diajukan',
  TERDISTRIBUSI: 'badge-diajukan',
  SIAP: 'badge-diterbitkan',
  DIBATALKAN: 'badge-ditolak',
}

function waktu(iso: string | null): string {
  return iso === null ? '—' : iso.slice(0, 19).replace('T', ' ')
}

function labelSesi(s: api.SessionRef): string {
  return `${s.tuk.code} · ${s.date.slice(0, 10)} · sesi ke-${s.sessionOrder}`
}

/**
 * Snapshot sesi — Track D.
 *
 * Halaman ini ada karena dua hal pada alur snapshot tidak dapat diperiksa lewat
 * API saja. Pertama, frasa amplop luring **hanya ditampilkan sekali** dan tidak
 * tersimpan di mana pun; tanpa layar yang menuntut pengakuan tercetak, frasa
 * itu berakhir di riwayat terminal. Kedua, pemisahan tugas penyusun dan
 * penyegel baru bermakna bila dua orang benar-benar dapat melihat snapshot yang
 * sama.
 */
export function SnapshotPage(): ReactNode {
  const [params, setParams] = useSearchParams()
  const dibuka = params.get('buka')

  const [rows, setRows] = useState<readonly api.Snapshot[]>([])
  const [error, setError] = useState<string | null>(null)

  const muat = useCallback(async () => {
    try {
      setRows(await api.list())
      setError(null)
    } catch (err) {
      setError(pesan(err))
    }
  }, [])

  useEffect(() => {
    void muat()
  }, [muat])

  if (dibuka !== null) {
    return (
      <Detail
        id={dibuka}
        onBack={() => {
          setParams({})
          void muat()
        }}
      />
    )
  }

  return (
    <div>
      <h1>Snapshot Sesi</h1>
      <p className="hint">
        Snapshot memuat <strong>salinan penuh</strong> isi ujian, bukan rujukan ke katalog. Itulah
        satu-satunya alasan snapshot ada: perubahan di Bank Soal tidak boleh merembes ke sesi yang
        sudah dijadwalkan. Setelah tersegel, isinya beku — perbaikan menghasilkan snapshot baru.
      </p>

      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {rows.length === 0 ? (
        <p className="dt-empty">
          Belum ada snapshot. Susun sirkuit di halaman Blueprint lalu jadikan snapshot dari sana.
        </p>
      ) : (
        <div className="dt">
          <table>
            <thead>
              <tr>
                <th>Sesi</th>
                <th>Status</th>
                <th>Disusun</th>
                <th>Disegel</th>
                <th>Sidik isi</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td>
                    {labelSesi(s.session)}
                    <br />
                    <span className="hint">{s.session.name}</span>
                  </td>
                  <td>
                    <span className={`badge ${STATE_CLASS[s.state]}`}>{s.state}</span>
                  </td>
                  <td>{s.builtBy ?? '—'}</td>
                  <td>
                    {s.sealedBy ?? '—'}
                    <br />
                    <span className="hint">{waktu(s.sealedAt)}</span>
                  </td>
                  <td>
                    <code>{s.contentHash.slice(0, 12)}…</code>
                  </td>
                  <td>
                    <button onClick={() => setParams({ buka: s.id })}>Buka</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Detail({ id, onBack }: { id: string; onBack: () => void }): ReactNode {
  const { user } = useAuth()
  const [row, setRow] = useState<api.SnapshotDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [frasa, setFrasa] = useState<api.SealResult | null>(null)
  const [catatan, setCatatan] = useState<string | null>(null)

  const muat = useCallback(async () => {
    try {
      setRow(await api.detail(id))
      setError(null)
    } catch (err) {
      setError(pesan(err))
    }
  }, [id])

  useEffect(() => {
    void muat()
  }, [muat])

  async function jalankan(aksi: () => Promise<unknown>, pesanSukses: string): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      await aksi()
      setCatatan(pesanSukses)
      await muat()
    } catch (err) {
      setError(pesan(err))
      setCatatan(null)
    } finally {
      setBusy(false)
    }
  }

  /**
   * Menyegel, lalu menyerahkan layar kepada lembar amplop.
   *
   * Tidak lewat `jalankan` karena hasilnya bukan catatan yang boleh hilang saat
   * halaman dimuat ulang: frasanya hanya ada pada respons ini.
   */
  async function segel(): Promise<void> {
    if (row === null) return
    setBusy(true)
    setError(null)
    try {
      setFrasa(await api.seal(row.id))
    } catch (err) {
      setError(pesan(err))
    } finally {
      setBusy(false)
    }
  }

  // Frasa amplop luring menutupi seluruh layar sampai diakui tercetak: ia tidak
  // dapat ditampilkan ulang, dan navigasi yang tidak sengaja menghilangkannya
  // selamanya.
  if (frasa !== null) {
    return (
      <LembarAmplop
        hasil={frasa}
        onSelesai={() => {
          setFrasa(null)
          void muat()
        }}
      />
    )
  }

  if (error !== null && row === null) {
    return (
      <div>
        <button onClick={onBack}>← Kembali</button>
        <p role="alert" className="error">
          {error}
        </p>
      </div>
    )
  }

  if (row === null) {
    return (
      <div>
        <button onClick={onBack}>← Kembali</button>
        <p className="hint">Memuat…</p>
      </div>
    )
  }

  const boleh = (a: api.SnapshotAction): boolean => row.aksiTersedia.includes(a)
  const penyusunSendiri = row.builtBy !== null && row.builtBy === user?.username

  return (
    <div>
      <button onClick={onBack}>← Kembali</button>

      <div className="version-head">
        <h1>{row.session.name}</h1>
        <span className={`badge ${STATE_CLASS[row.state]}`}>{row.state}</span>
      </div>
      <p className="hint">
        {row.session.tuk.name} ({row.session.tuk.code}) · {row.session.date.slice(0, 10)} · sesi ke-
        {row.session.sessionOrder} · sidik isi <code>{row.contentHash.slice(0, 16)}…</code>
      </p>

      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {catatan !== null && <p className="notice">{catatan}</p>}

      <ul className="ringkas-list">
        <li>
          <span>Disusun</span>
          <span>
            {row.builtBy ?? '—'} · {waktu(row.createdAt)}
          </span>
        </li>
        <li>
          <span>Disegel</span>
          <span>
            {row.sealedBy ?? '—'} · {waktu(row.sealedAt)}
          </span>
        </li>
        <li>
          <span>Paket terenkripsi</span>
          <span>
            {row.payloadBytes === null ? '—' : `${Math.ceil(row.payloadBytes / 1024)} KB`}
            {row.payloadObjectKey === null ? '' : ' · tersimpan'}
          </span>
        </li>
        <li>
          <span>Didistribusikan</span>
          <span>{waktu(row.distributedAt)}</span>
        </li>
        <li>
          <span>Kunci dilepas</span>
          <span>
            {row.keyReleasedBy ?? '—'} · {waktu(row.keyReleasedAt)}
          </span>
        </li>
      </ul>

      {row.offlineOpenedAt !== null && (
        <p className="peringatan">
          Amplop luring dibuka {waktu(row.offlineOpenedAt)} oleh {row.offlineOpenedBy}. Alasan:{' '}
          {row.offlineOpenedReason}. Sejak saat itu kunci sesi ini ada di tangan manusia, bukan
          hanya di dalam sistem.
        </p>
      )}

      {row.cancelledAt !== null && (
        <p className="notice">
          Dibatalkan {waktu(row.cancelledAt)}. Alasan: {row.cancelledReason}
        </p>
      )}

      <h2>Tindakan</h2>
      <div className="page-actions">
        <button disabled={busy || !boleh('segel') || penyusunSendiri} onClick={() => void segel()}>
          Segel
        </button>

        <button
          disabled={busy || !boleh('distribusi')}
          onClick={() => void jalankan(() => api.distribute(row.id), 'Paket didistribusikan.')}
        >
          Distribusikan
        </button>

        <button
          disabled={busy || !boleh('lepas-kunci') || !row.pelepasanKunci.ok}
          onClick={() =>
            void jalankan(
              () => api.releaseKey(row.id),
              'Kunci dilepas. Kuncinya tidak ditampilkan di sini; tidak ada yang perlu mengetiknya.',
            )
          }
        >
          Lepas kunci
        </button>
      </div>

      {boleh('segel') && penyusunSendiri && (
        <p className="notice">
          Anda yang menyusun snapshot ini, sehingga tidak dapat menyegelnya. Penyegelan menyatakan
          satu sesi siap diujikan seluruh peserta di TUK itu — satu orang tidak cukup.
        </p>
      )}

      {!row.pelepasanKunci.ok && <p className="hint">{row.pelepasanKunci.message}</p>}

      {boleh('buka-amplop') && (
        <FormAmplop
          busy={busy}
          onBuka={(f, alasan) =>
            void jalankan(
              () => api.openEnvelope(row.id, f, alasan),
              'Amplop luring dibuka dan tercatat sebagai insiden.',
            )
          }
        />
      )}

      {boleh('batal') && (
        <FormBatal
          busy={busy}
          onBatal={(alasan) =>
            void jalankan(() => api.cancel(row.id, alasan), 'Snapshot dibatalkan.')
          }
        />
      )}

      {/* Jumlah rotasi diambil dari yang disembunyikan bila isinya ditahan.
          Menuliskan `stations.length` begitu saja membuat layar menyatakan
          "0 rotasi" untuk snapshot berisi dua belas station — dan sirkuit yang
          dilaporkan kosong padahal terisi adalah kekeliruan yang lebih buruk
          daripada tidak menampilkan apa-apa. */}
      <h2>Isi sirkuit — {row.stationsDisembunyikan ?? row.stations.length} rotasi</h2>

      {row.stationsDisembunyikan !== undefined && (
        <p className="notice">
          Kode dan judul station tidak ditampilkan untuk cakupan TUK. Itu isi ujian, dan Anda berada
          di gedung yang sama dengan peserta pada hari pelaksanaan.
        </p>
      )}

      <div className="dt" hidden={row.stationsDisembunyikan !== undefined}>
        <table>
          <thead>
            <tr>
              <th>Rotasi</th>
              <th>Station</th>
              <th>Judul</th>
              <th>Kategori</th>
            </tr>
          </thead>
          <tbody>
            {row.stations.map((s) => (
              <tr key={s.rotationNumber}>
                <td>{s.rotationNumber}</td>
                <td>
                  <code>{s.station.sourceCode}</code>{' '}
                  <span className="hint">v{s.station.version}</span>
                </td>
                <td>{s.station.title}</td>
                <td>{s.station.categoryName}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Frasa amplop luring — ditampilkan sekali, lalu tidak pernah lagi.
 *
 * Dicetak, disegel, dan diserahkan kepada koordinator TUK. Frasanya tidak
 * tersimpan di basis data dalam bentuk apa pun: yang tersimpan hanyalah kunci
 * data yang terbungkus olehnya.
 */
function LembarAmplop({
  hasil,
  onSelesai,
}: {
  hasil: api.SealResult
  onSelesai: () => void
}): ReactNode {
  const [diakui, setDiakui] = useState(false)

  return (
    <div>
      <p className="peringatan no-print">{hasil.peringatan}</p>

      <div className="kartu">
        <div className="kartu-kepala">
          <span>Amplop kunci luring</span>
          <span>{hasil.session.tuk.code}</span>
        </div>
        <div className="kartu-nama">{hasil.session.name}</div>
        <p className="kartu-kaki">
          {hasil.session.date.slice(0, 10)} · sesi ke-{hasil.session.sessionOrder} ·{' '}
          {hasil.session.tuk.name}
        </p>
        <div className="kartu-kode">{hasil.frasaAmplopLuring}</div>
        <p className="kartu-catatan">
          Dipakai hanya bila WAN putus tepat saat kunci seharusnya dilepas. Membukanya tercatat
          sebagai insiden dan menuntut alasan tertulis.
        </p>
      </div>

      <div className="page-actions no-print">
        <button onClick={() => window.print()}>Cetak</button>
        <label className="inline-label">
          <input type="checkbox" checked={diakui} onChange={(e) => setDiakui(e.target.checked)} />
          Sudah dicetak, disegel, dan diserahkan kepada koordinator TUK
        </label>
        <button disabled={!diakui} onClick={onSelesai}>
          Selesai
        </button>
      </div>
    </div>
  )
}

function FormAmplop({
  busy,
  onBuka,
}: {
  busy: boolean
  onBuka: (frasa: string, alasan: string) => void
}): ReactNode {
  const [frasa, setFrasa] = useState('')
  const [alasan, setAlasan] = useState('')

  return (
    <details>
      <summary>Buka amplop kunci luring</summary>
      <p className="hint">
        Kontingensi bila WAN putus tepat saat kunci seharusnya dilepas. Alasannya wajib: sejak
        amplop dibuka, kunci sesi ada di tangan manusia, dan insiden tanpa keterangan tidak dapat
        ditinjau.
      </p>
      <div className="terbit-window">
        <label>
          Frasa amplop
          <input value={frasa} onChange={(e) => setFrasa(e.target.value)} />
        </label>
        <label>
          Alasan
          <input value={alasan} onChange={(e) => setAlasan(e.target.value)} />
        </label>
      </div>
      <div className="page-actions">
        <button
          disabled={busy || frasa.trim() === '' || alasan.trim() === ''}
          onClick={() => onBuka(frasa.trim(), alasan.trim())}
        >
          Buka amplop
        </button>
      </div>
    </details>
  )
}

function FormBatal({
  busy,
  onBatal,
}: {
  busy: boolean
  onBatal: (alasan: string) => void
}): ReactNode {
  const [alasan, setAlasan] = useState('')

  return (
    <details>
      <summary>Batalkan snapshot</summary>
      <p className="hint">
        Riwayat paparan station ikut dihapus: sesi yang dibatalkan tidak pernah berjalan, dan
        station-nya harus dapat dipakai lagi.
      </p>
      <div className="terbit-window">
        <label>
          Alasan
          <input value={alasan} onChange={(e) => setAlasan(e.target.value)} />
        </label>
      </div>
      <div className="page-actions">
        <button disabled={busy || alasan.trim() === ''} onClick={() => onBatal(alasan.trim())}>
          Batalkan
        </button>
      </div>
    </details>
  )
}
